import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { BRAND, wsUrl, API } from "@/lib/appApi";
import { toast } from "sonner";
import { ArrowLeft, Flag, Users, MessagesSquare, Ban, ShieldCheck, Circle, Eye, EyeOff, Trash2, Plus, Crown, Maximize2, Minimize2, Download, Settings, UserX } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MonitorWindow from "@/components/MonitorWindow";

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [online, setOnline] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [bans, setBans] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [retention, setRetention] = useState("off");
  const [shotRetention, setShotRetention] = useState("off");
  const [retentionOptions, setRetentionOptions] = useState(["off", "daily", "weekly", "monthly", "yearly"]);
  const [viewSession, setViewSession] = useState(null);
  const [newAdmin, setNewAdmin] = useState("");

  // monitoring
  const [maintenance, setMaintenance] = useState(false);
  const [siteCode, setSiteCode] = useState({ ga_id: "", search_console: "", head_js: "", body_js: "" });
  const [seo, setSeo] = useState({ title: "", description: "", keywords: "", og_title: "", og_description: "", og_image: "", robots_index: true });
  const [confirmDelete, setConfirmDelete] = useState(null); // { email, name }
  const [viewShot, setViewShot] = useState(null); // report_id for full screenshot
  const [banTarget, setBanTarget] = useState(null); // { email, ip }
  const BAN_REASONS = ["Nudity to a minor / illegal content", "Harassment or abuse", "Non-consensual recording", "Sexual content shown to unwilling users", "Underage user (under 18)", "Spam or advertising", "Other violation of our Terms"];
  const [banReason, setBanReason] = useState(BAN_REASONS[0]);
  const [banDetail, setBanDetail] = useState("");
  const [monitors, setMonitors] = useState([]); // [{user, stream}]
  const wsRef = useRef(null);
  const iceRef = useRef([{ urls: "stun:stun.l.google.com:19302" }]);
  const monPcs = useRef({}); // user_id -> RTCPeerConnection

  const loadAll = useCallback(async () => {
    try {
      const [r, u, s, b, a, d, st, on] = await Promise.all([
        api.get("/admin/reports"), api.get("/admin/users"), api.get("/admin/chat-sessions"),
        api.get("/admin/bans"), api.get("/admin/whitelist"), api.get("/admin/deleted-users"),
        api.get("/admin/settings"), api.get("/admin/online"),
      ]);
      setReports(r.data); setUsers(u.data); setSessions(s.data); setBans(b.data); setAdmins(a.data);
      setDeleted(d.data); setRetention(st.data.log_retention); setShotRetention(st.data.screenshot_retention || "off"); setRetentionOptions(st.data.options);
      setOnline((on.data || []).filter((x) => !x.is_admin));
    } catch (e) { toast.error("Failed to load admin data"); }
  }, []);

  // REST polling fallback so the Online list works even if the WS push is delayed/unavailable
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const on = await api.get("/admin/online");
        setOnline((on.data || []).filter((x) => !x.is_admin));
      } catch (e) {}
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const exportData = (name, rows) => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${name}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const saveRetention = async (val) => {
    try { await api.post("/admin/settings", { log_retention: val }); setRetention(val); toast.success("Log retention updated"); }
    catch (e) { toast.error("Failed to update"); }
  };
  const saveShotRetention = async (val) => {
    try { await api.post("/admin/settings", { screenshot_retention: val }); setShotRetention(val); toast.success("Screenshot retention updated"); }
    catch (e) { toast.error("Failed to update"); }
  };
  const clearLogsNow = async () => {
    try { const res = await api.post("/admin/logs/clear"); toast.success(`Cleared ${res.data.deleted} chat logs`); loadAll(); }
    catch (e) { toast.error("Failed to clear logs"); }
  };

  useEffect(() => {
    api.get("/admin/maintenance").then((r) => setMaintenance(r.data.maintenance_mode)).catch(() => {});
    api.get("/site-code").then((r) => setSiteCode(r.data)).catch(() => {});
    api.get("/seo").then((r) => setSeo(r.data)).catch(() => {});
  }, []);
  const saveSiteCode = async () => {
    try { await api.post("/admin/site-code", siteCode); toast.success("Site code saved — changes apply on next page load"); }
    catch (e) { toast.error("Failed to save site code"); }
  };
  const saveSeo = async () => {
    try { await api.post("/admin/seo", seo); toast.success("SEO settings saved — applies on next page load"); }
    catch (e) { toast.error("Failed to save SEO settings"); }
  };
  const toggleMaintenance = async (v) => {
    try {
      await api.post("/admin/maintenance", { enabled: v });
      setMaintenance(v);
      toast.success(v ? "Maintenance mode ON — non-admin users kicked out" : "Maintenance mode OFF — site is live");
    } catch (e) { toast.error("Failed to update maintenance mode"); }
  };

  const handleMonitorSignal = useCallback(async (fromUser, payload) => {
    const pc = monPcs.current[fromUser];
    if (!pc) return;
    if (payload.sdp) {
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      if (payload.sdp.type === "offer") {
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        wsRef.current?.send(JSON.stringify({ type: "monitor_signal", target: fromUser, payload: { sdp: pc.localDescription } }));
      }
    } else if (payload.candidate) {
      try { await pc.addIceCandidate(new RTCIceCandidate(payload.candidate)); } catch (e) {}
    }
  }, []);

  useEffect(() => {
    loadAll();
    let closedByUnmount = false;
    let hb = null;
    let reconnectTimer = null;

    const clearHb = () => { if (hb) { clearInterval(hb); hb = null; } };

    const connect = async () => {
      try {
        const cfg = await api.get("/config");
        iceRef.current = cfg.data.ice_servers;
        const res = await api.get("/auth/ws-token");
        const ws = new WebSocket(wsUrl(res.data.token));
        wsRef.current = ws;
        ws.onopen = () => {
          clearHb();
          hb = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
          }, 25000);
        };
        ws.onmessage = (e) => {
          const msg = JSON.parse(e.data);
          if (msg.type === "online_update") setOnline(msg.users.filter((x) => !x.is_admin));
          else if (msg.type === "monitor_signal") handleMonitorSignal(msg.from, msg.payload);
          else if (msg.type === "monitor_unavailable") toast.error("User is not streaming right now.");
        };
        ws.onclose = () => {
          clearHb();
          if (!closedByUnmount) reconnectTimer = setTimeout(connect, 3000);
        };
      } catch (e) {
        if (!closedByUnmount) reconnectTimer = setTimeout(connect, 3000);
      }
    };
    connect();

    return () => {
      closedByUnmount = true;
      clearHb();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) try { wsRef.current.close(); } catch (e) {}
    };
  }, [loadAll, handleMonitorSignal]);

  const startMonitor = async (u) => {
    if (monPcs.current[u.user_id]) return; // already watching
    const pc = new RTCPeerConnection({ iceServers: iceRef.current });
    monPcs.current[u.user_id] = pc;
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });
    pc.ontrack = (e) => setMonitors((m) => m.map((x) => x.user.user_id === u.user_id ? { ...x, stream: e.streams[0] } : x));
    pc.onicecandidate = (e) => {
      if (e.candidate) wsRef.current?.send(JSON.stringify({ type: "monitor_signal", target: u.user_id, payload: { candidate: e.candidate } }));
    };
    setMonitors((m) => [...m, { user: u, stream: null }]);
    wsRef.current?.send(JSON.stringify({ type: "monitor_start", target: u.user_id }));
  };

  const stopMonitor = (userId) => {
    const pc = monPcs.current[userId];
    if (pc) { try { pc.close(); } catch (e) {} delete monPcs.current[userId]; }
    wsRef.current?.send(JSON.stringify({ type: "monitor_stop", target: userId }));
    setMonitors((m) => m.filter((x) => x.user.user_id !== userId));
  };

  const ban = async (payload) => {
    try { await api.post("/admin/ban", payload); toast.success("Banned"); loadAll(); }
    catch (e) { toast.error("Ban failed"); }
  };
  const openBan = (email, ip) => { setBanReason(BAN_REASONS[0]); setBanDetail(""); setBanTarget({ email, ip }); };
  const submitBan = async () => {
    const reason = `${banReason}${banDetail.trim() ? " — " + banDetail.trim() : ""}`;
    await ban({ email: banTarget.email, ip: banTarget.ip, reason });
    setBanTarget(null);
  };
  const unban = async (payload) => {
    try { await api.post("/admin/unban", payload); toast.success("Unbanned"); loadAll(); }
    catch (e) { toast.error("Unban failed"); }
  };
  const deleteUser = async (email) => {
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      toast.success("User deleted — they can register again");
      setConfirmDelete(null); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const resolveReport = async (id) => {
    try { await api.post(`/admin/reports/${id}/resolve`); loadAll(); } catch (e) {}
  };
  const addAdmin = async () => {
    if (!newAdmin.trim()) return;
    try { await api.post("/admin/whitelist", { email: newAdmin.trim() }); setNewAdmin(""); toast.success("Admin added"); loadAll(); }
    catch (e) { toast.error("Failed"); }
  };
  const removeAdmin = async (email) => {
    try { await api.delete(`/admin/whitelist/${encodeURIComponent(email)}`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <img src={BRAND.logo} alt="logo" className="w-8 h-8 rounded-full" />
          <div>
            <span className="font-heading font-extrabold">Admin Console</span>
            <p className="text-xs text-muted-foreground">Random Cam Chat</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate("/app")} data-testid="admin-to-app"><ArrowLeft className="w-4 h-4 mr-1" /> App</Button>
          <Button variant="outline" onClick={logout} className="border-border bg-secondary">Log out</Button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        <Tabs defaultValue="reports">
          <TabsList className="bg-secondary border border-border">
            <TabsTrigger value="reports" data-testid="tab-reports"><Flag className="w-4 h-4 mr-1.5" /> Reports</TabsTrigger>
            <TabsTrigger value="online" data-testid="tab-online"><Circle className="w-4 h-4 mr-1.5" /> Online ({online.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger value="chats" data-testid="tab-chats"><MessagesSquare className="w-4 h-4 mr-1.5" /> Chat Logs</TabsTrigger>
            <TabsTrigger value="bans" data-testid="tab-bans"><Ban className="w-4 h-4 mr-1.5" /> Bans</TabsTrigger>
            <TabsTrigger value="deleted" data-testid="tab-deleted"><UserX className="w-4 h-4 mr-1.5" /> Deleted</TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins"><ShieldCheck className="w-4 h-4 mr-1.5" /> Admins</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="w-4 h-4 mr-1.5" /> Settings</TabsTrigger>
          </TabsList>

          {/* Reports */}
          <TabsContent value="reports" className="mt-4">
            <div className="flex justify-end mb-2"><Button size="sm" variant="outline" className="border-border bg-secondary" onClick={() => exportData("reports", reports)} data-testid="export-reports"><Download className="w-4 h-4 mr-1" /> Export</Button></div>
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Reported</TableHead><TableHead>Reporter</TableHead><TableHead>Reason</TableHead>
                    <TableHead>Cam</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No reports yet.</TableCell></TableRow>}
                  {reports.map((r) => (
                    <TableRow key={r.report_id} className="border-border">
                      <TableCell className="text-sm">{r.reported_email || r.reported_user_id}<div className="text-xs text-muted-foreground">{r.reported_ip}</div></TableCell>
                      <TableCell className="text-sm">{r.reporter_email}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{r.reason}</TableCell>
                      <TableCell>
                        {r.has_screenshot
                          ? <img src={`${API}/admin/reports/${r.report_id}/screenshot`} alt="cam evidence" onClick={() => setViewShot(r.report_id)} data-testid={`shot-${r.report_id}`} className="w-20 h-14 object-cover rounded-md border border-border cursor-pointer hover:opacity-80" />
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell><Badge variant={r.status === "open" ? "destructive" : "secondary"}>{r.status}</Badge></TableCell>
                      <TableCell className="text-right space-x-1">
                        {r.room_id && <Button size="sm" variant="ghost" onClick={() => setViewSession(r.room_id)} data-testid={`view-chat-${r.report_id}`}>View chat</Button>}
                        {r.reported_email && <Button size="sm" variant="outline" className="border-destructive text-destructive" onClick={() => openBan(r.reported_email, r.reported_ip)} data-testid={`ban-${r.report_id}`}>Ban</Button>}
                        {r.reported_email && <Button size="sm" variant="outline" className="border-destructive text-destructive" onClick={() => setConfirmDelete({ email: r.reported_email, name: r.reported_email })} data-testid={`delete-${r.report_id}`}>Delete</Button>}
                        {r.status === "open" && <Button size="sm" variant="ghost" onClick={() => resolveReport(r.report_id)}>Resolve</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Online */}
          <TabsContent value="online" className="mt-4">
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>User</TableHead><TableHead>Gender</TableHead><TableHead>State</TableHead><TableHead>Chatting with</TableHead><TableHead className="text-right">Monitor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {online.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nobody online right now.</TableCell></TableRow>}
                  {online.map((u) => (
                    <TableRow key={u.user_id} className="border-border">
                      <TableCell className="text-sm">{u.name}<div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                      <TableCell className="text-sm">{u.gender || "—"} {u.is_premium && <Crown className="w-3 h-3 inline text-amber-400" />}</TableCell>
                      <TableCell><Badge variant="secondary">{u.state}</Badge></TableCell>
                      <TableCell className="text-sm" data-testid={`online-partner-${u.user_id}`}>
                        {u.partner_name ? <>{u.partner_name}<div className="text-xs text-muted-foreground">{u.partner_email}</div></> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="border-border" onClick={() => startMonitor(u)} data-testid={`monitor-${u.user_id}`}>
                          <Eye className="w-4 h-4 mr-1" /> Watch
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Click <b>Watch</b> to open a live camera window. You can open several at once and drag each window around to monitor multiple users at the same time (best-effort feed).</p>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="mt-4">
            <div className="flex justify-end mb-2"><Button size="sm" variant="outline" className="border-border bg-secondary" onClick={() => exportData("users", users)} data-testid="export-users"><Download className="w-4 h-4 mr-1" /> Export</Button></div>
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>User</TableHead><TableHead>IP</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} className="border-border">
                      <TableCell className="text-sm">{u.name}<div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{u.last_ip}</TableCell>
                      <TableCell>{u.is_premium ? <Badge className="brand-gradient-bg text-white border-0">Premium</Badge> : <Badge variant="secondary">Free</Badge>}</TableCell>
                      <TableCell>{u.banned ? <Badge variant="destructive">Banned</Badge> : (u.online ? <span className="text-green-400 text-xs">● online</span> : <span className="text-muted-foreground text-xs">offline</span>)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {u.banned
                          ? <Button size="sm" variant="ghost" onClick={() => unban({ email: u.email, ip: u.last_ip })}>Unban</Button>
                          : <Button size="sm" variant="outline" className="border-destructive text-destructive" onClick={() => openBan(u.email, u.last_ip)} data-testid={`ban-user-${u.user_id}`}>Ban</Button>}
                        <Button size="sm" variant="outline" className="border-destructive text-destructive" onClick={() => setConfirmDelete({ email: u.email, name: u.name })} data-testid={`delete-user-${u.user_id}`}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Chat logs */}
          <TabsContent value="chats" className="mt-4">
            <div className="flex justify-end mb-2"><Button size="sm" variant="outline" className="border-border bg-secondary" onClick={() => exportData("chat_logs", sessions)} data-testid="export-chats"><Download className="w-4 h-4 mr-1" /> Export</Button></div>
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Participants</TableHead><TableHead>Messages</TableHead><TableHead>Started</TableHead><TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No chat sessions yet.</TableCell></TableRow>}
                  {sessions.map((s) => (
                    <TableRow key={s.room_id} className="border-border">
                      <TableCell className="text-sm">{(s.participants || []).map((p) => p.name).join(" ↔ ")}</TableCell>
                      <TableCell className="text-sm">{(s.messages || []).length}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="outline" className="border-border" onClick={() => setViewSession(s.room_id)} data-testid={`view-session-${s.room_id}`}>Open</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Bans */}
          <TabsContent value="bans" className="mt-4">
            <div className="flex justify-end mb-2"><Button size="sm" variant="outline" className="border-border bg-secondary" onClick={() => exportData("bans", bans)} data-testid="export-bans"><Download className="w-4 h-4 mr-1" /> Export</Button></div>
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Email</TableHead><TableHead>IP</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bans.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No bans.</TableCell></TableRow>}
                  {bans.map((b) => (
                    <TableRow key={b.ban_id} className="border-border">
                      <TableCell className="text-sm">{b.email || "—"}</TableCell>
                      <TableCell className="text-sm">{b.ip || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{b.reason}</TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => unban({ email: b.email, ip: b.ip })} data-testid={`unban-${b.ban_id}`}>Unban</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Admins */}
          <TabsContent value="admins" className="mt-4 max-w-xl">
            <div className="flex gap-2 mb-4">
              <Input placeholder="new.admin@gmail.com" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} className="bg-secondary border-border" data-testid="new-admin-input" />
              <Button onClick={addAdmin} className="brand-gradient-bg text-white border-0" data-testid="add-admin-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>Email</TableHead><TableHead>Source</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {admins.map((a) => (
                    <TableRow key={a.email} className="border-border">
                      <TableCell className="text-sm">{a.email}</TableCell>
                      <TableCell><Badge variant="secondary">{a.source}</Badge></TableCell>
                      <TableCell className="text-right">
                        {a.source === "whitelist" && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeAdmin(a.email)} data-testid={`remove-admin-${a.email}`}><Trash2 className="w-4 h-4" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Deleted users */}
          <TabsContent value="deleted" className="mt-4">
            <div className="flex justify-end mb-2"><Button size="sm" variant="outline" className="border-border bg-secondary" onClick={() => exportData("deleted_users", deleted)} data-testid="export-deleted"><Download className="w-4 h-4 mr-1" /> Export</Button></div>
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Country</TableHead><TableHead>Had premium</TableHead><TableHead>Deleted</TableHead></TableRow></TableHeader>
                <TableBody>
                  {deleted.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No deleted accounts.</TableCell></TableRow>}
                  {deleted.map((d) => (
                    <TableRow key={d.user_id} className="border-border">
                      <TableCell className="text-sm">{d.email}</TableCell>
                      <TableCell className="text-sm">{d.name || "—"}</TableCell>
                      <TableCell className="text-sm">{d.country || "—"}</TableCell>
                      <TableCell>{d.had_premium ? <Badge className="brand-gradient-bg text-white border-0">Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(d.deleted_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Login records are removed so the email can be reused. Bans (if any) persist to prevent re-registration.</p>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings" className="mt-4 max-w-xl space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-bold mb-1">Maintenance mode</p>
                  <p className="text-sm text-muted-foreground">Shows a maintenance notice on the site and immediately disconnects all non-admin users. Admins keep full access.</p>
                </div>
                <Switch checked={maintenance} onCheckedChange={toggleMaintenance} data-testid="maintenance-switch" />
              </div>
              {maintenance && <p className="text-xs text-amber-400 mt-3">⚠️ The site is currently in maintenance mode — regular users cannot connect.</p>}
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="font-heading font-bold mb-1">Chat log retention</p>
              <p className="text-sm text-muted-foreground mb-4">Automatically delete stored text chat logs after this period. Runs hourly.</p>
              <div className="flex items-center gap-3">
                <Select value={retention} onValueChange={saveRetention}>
                  <SelectTrigger className="bg-secondary border-border w-48" data-testid="retention-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {retentionOptions.map((o) => <SelectItem key={o} value={o}>{o === "off" ? "Off (keep forever)" : o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive/10" onClick={clearLogsNow} data-testid="clear-logs-btn"><Trash2 className="w-4 h-4 mr-1" /> Clear all logs now</Button>
              </div>
              <div className="mt-5 pt-5 border-t border-border">
                <p className="font-heading font-bold mb-1">Report screenshot retention</p>
                <p className="text-sm text-muted-foreground mb-4">Auto-delete stored moderation snapshots (reported users' cam frames) after this period. Keeps storage lean and is privacy-friendly. Runs hourly.</p>
                <Select value={shotRetention} onValueChange={saveShotRetention}>
                  <SelectTrigger className="bg-secondary border-border w-48" data-testid="shot-retention-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {retentionOptions.map((o) => <SelectItem key={o} value={o}>{o === "off" ? "Off (keep forever)" : o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
              <div>
                <p className="font-heading font-bold mb-1">Site code &amp; analytics</p>
                <p className="text-sm text-muted-foreground">Injected on every page for all visitors. Applies on next page load.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Google Analytics Measurement ID</Label>
                <Input value={siteCode.ga_id} onChange={(e) => setSiteCode({ ...siteCode, ga_id: e.target.value })} placeholder="G-XXXXXXXXXX" className="bg-secondary border-border" data-testid="ga-id-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Search Console verification (meta content)</Label>
                <Input value={siteCode.search_console} onChange={(e) => setSiteCode({ ...siteCode, search_console: e.target.value })} placeholder="content value from the google-site-verification meta tag" className="bg-secondary border-border" data-testid="gsc-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Custom header code (&lt;head&gt;)</Label>
                <Textarea value={siteCode.head_js} onChange={(e) => setSiteCode({ ...siteCode, head_js: e.target.value })} placeholder="Paste <script> or tags to load in the page head" className="bg-secondary border-border font-mono text-xs min-h-[90px]" data-testid="head-js-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Custom footer code (end of &lt;body&gt;)</Label>
                <Textarea value={siteCode.body_js} onChange={(e) => setSiteCode({ ...siteCode, body_js: e.target.value })} placeholder="Paste <script> or tags to load at the end of the body" className="bg-secondary border-border font-mono text-xs min-h-[90px]" data-testid="body-js-input" />
              </div>
              <Button onClick={saveSiteCode} className="brand-gradient-bg text-white border-0" data-testid="save-site-code-btn">Save site code</Button>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
              <div>
                <p className="font-heading font-bold mb-1">SEO configuration</p>
                <p className="text-sm text-muted-foreground">Controls the page title, meta description and social share preview. Applies on next page load.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Page title</Label>
                <Input value={seo.title || ""} onChange={(e) => setSeo({ ...seo, title: e.target.value })} className="bg-secondary border-border" data-testid="seo-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Meta description</Label>
                <Textarea value={seo.description || ""} onChange={(e) => setSeo({ ...seo, description: e.target.value })} className="bg-secondary border-border text-sm min-h-[70px]" data-testid="seo-description-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Keywords (comma separated)</Label>
                <Input value={seo.keywords || ""} onChange={(e) => setSeo({ ...seo, keywords: e.target.value })} className="bg-secondary border-border" data-testid="seo-keywords-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Social share title (Open Graph)</Label>
                <Input value={seo.og_title || ""} onChange={(e) => setSeo({ ...seo, og_title: e.target.value })} className="bg-secondary border-border" data-testid="seo-ogtitle-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Social share description</Label>
                <Textarea value={seo.og_description || ""} onChange={(e) => setSeo({ ...seo, og_description: e.target.value })} className="bg-secondary border-border text-sm min-h-[70px]" data-testid="seo-ogdesc-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Social share image URL</Label>
                <Input value={seo.og_image || ""} onChange={(e) => setSeo({ ...seo, og_image: e.target.value })} placeholder="https://..." className="bg-secondary border-border" data-testid="seo-ogimage-input" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Allow search engines to index the site</p>
                  <p className="text-xs text-muted-foreground">Turn off to hide the site from Google (noindex).</p>
                </div>
                <Switch checked={!!seo.robots_index} onCheckedChange={(v) => setSeo({ ...seo, robots_index: v })} data-testid="seo-robots-switch" />
              </div>
              <Button onClick={saveSeo} className="brand-gradient-bg text-white border-0" data-testid="save-seo-btn">Save SEO settings</Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <ChatLogDialog roomId={viewSession} onClose={() => setViewSession(null)} />
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Delete user?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This permanently removes <b className="text-foreground">{confirmDelete?.email}</b>'s login and sessions.
              Unlike a ban, they will be able to register again with the same email.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-btn">Cancel</Button>
              <Button className="bg-destructive text-white hover:bg-destructive/90" onClick={() => deleteUser(confirmDelete.email)} data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete user
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!viewShot} onOpenChange={(o) => !o && setViewShot(null)}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Reported cam snapshot</DialogTitle></DialogHeader>
          {viewShot && <img src={`${API}/admin/reports/${viewShot}/screenshot`} alt="cam evidence" className="w-full rounded-lg border border-border" data-testid="shot-full" />}
          <p className="text-xs text-muted-foreground">Captured from the reported user's camera at the moment the report was filed.</p>
        </DialogContent>
      </Dialog>
      <Dialog open={!!banTarget} onOpenChange={(o) => !o && setBanTarget(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Ban className="w-5 h-5 text-destructive" /> Ban user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Banning <b className="text-foreground">{banTarget?.email || banTarget?.ip}</b>. Pick a reason — it's included in the suspension email sent to the member.</p>
            <Label className="text-sm">Reason</Label>
            <div className="grid gap-1.5 max-h-56 overflow-y-auto">
              {BAN_REASONS.map((r) => (
                <button key={r} onClick={() => setBanReason(r)} data-testid={`ban-reason-${r.slice(0, 6)}`}
                  className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${banReason === r ? "border-primary bg-primary/10" : "border-border bg-secondary hover:bg-accent"}`}>
                  {r}
                </button>
              ))}
            </div>
            <Textarea placeholder="Add details (optional, also shown in the email)" value={banDetail} onChange={(e) => setBanDetail(e.target.value)} className="bg-secondary border-border text-sm" data-testid="ban-detail-input" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setBanTarget(null)} data-testid="cancel-ban-btn">Cancel</Button>
              <Button className="bg-destructive text-white hover:bg-destructive/90" onClick={submitBan} data-testid="confirm-ban-btn"><Ban className="w-4 h-4 mr-1" /> Ban &amp; notify</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {monitors.map((m, i) => (
        <MonitorWindow key={m.user.user_id} user={m.user} stream={m.stream} index={i} onClose={stopMonitor} />
      ))}
    </div>
  );
}

function ChatLogDialog({ roomId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!roomId) { setData(null); return; }
    api.get(`/admin/chat-sessions/${roomId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [roomId]);
  return (
    <Dialog open={!!roomId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Conversation log</DialogTitle></DialogHeader>
        <div className="max-h-96 overflow-y-auto space-y-2">
          {data?.participants && <p className="text-xs text-muted-foreground">{data.participants.map((p) => `${p.name} (${p.email})`).join("  ·  ")}</p>}
          {(data?.messages || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No messages in this session.</p>}
          {(data?.messages || []).map((m, i) => (
            <div key={i} className="text-sm">
              <span className="font-medium text-primary">{m.from_name}:</span> <span>{m.text}</span>
              <span className="text-xs text-muted-foreground ml-2">{new Date(m.ts).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
